import { useEffect, useId, useState } from "react";
import type { FormEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { demoApi, problemMessage } from "@/api/client";
import type { BusinessEntity, PartnerSplitReport } from "@/api/types";
import "./ReportsPartnerSplits.css";

type ReportStatus = "idle" | "loading" | "ready" | "error";

/** Tolerance for the "sums to 100%" flag — guards against binary floating-point noise on
 *  the client-side sum (e.g. 33.33 + 33.33 + 33.34), never widens what the server itself
 *  considers balanced (it enforces nothing — see `PartnerSplitsReportRepository`'s own
 *  remarks). */
const BALANCED_TOLERANCE = 0.005;

/**
 * Partner split allocation — Reports' percentage roster: who owns what share of one
 * entity's proceeds, right now. ef-053-5, ADR 0009 §6 ("Reports is one picker" — a report
 * renders inside the template at `/reports/{slug}`, no area tab strip of its own; "All
 * reports" below is the only way back).
 *
 * <p>
 * ENTITY IS REQUIRED, SO THIS SCREEN CARRIES ITS OWN PICKER — unlike a whole-farm-default
 * report, `PartnerSplitsReportEndpoints` has no "resolve the tenant's own single/oldest
 * entity" fallback (D2, same posture `income-statement`'s own contract takes). Same shape
 * `ReportsScheduleF.tsx`'s own "Entity" picker already uses: seeded once from the
 * server's own entity list, a "Show" button fetches on click.
 * </p>
 *
 * <p>
 * NOTHING IS FETCHED UNTIL Show IS PRESSED — same reasoning `ReportsScheduleF.tsx`'s own
 * remarks give: a percentage roster read by an owner who has chosen an entity should not
 * show a number for an entity nobody asked about. Only the entity list (for the picker
 * itself) loads eagerly.
 * </p>
 *
 * <p>
 * NO CAP, NO SYNTHESIZED TOTAL ROW ON THE SERVER — two active splits summing past 100%
 * both still render (D6/0075). This screen renders a client-side-only Total row as a
 * courtesy and flags it visually/`role="status"` when the sum is not 100%, but never
 * drops or rewrites a row to force it to balance.
 * </p>
 */
export function ReportsPartnerSplits() {
  const entityFieldId = useId();

  const [entities, setEntities] = useState<BusinessEntity[] | null>(null);
  const [entityId, setEntityId] = useState<string>("");

  const [report, setReport] = useState<PartnerSplitReport | null>(null);
  const [status, setStatus] = useState<ReportStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    demoApi
      .getEntities(controller.signal)
      .then((rows) => {
        setEntities(rows);
        // Seeded once, from the server's own list — never left blank when there is
        // exactly one entity to pick, the common single-entity farm this booth demo
        // itself models (db/seed/0002_seed_demo.sql).
        setEntityId((current) => current || (rows[0]?.id ?? ""));
      })
      .catch(() => setEntities([]));
    return () => controller.abort();
  }, []);

  const canSubmit = entityId !== "" && status !== "loading";

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;

    setStatus("loading");
    setError(null);
    try {
      const result = await demoApi.getPartnerSplitsReport({ entityId });
      setReport(result);
      setStatus("ready");
    } catch (err) {
      setReport(null);
      setStatus("error");
      setError(problemMessage(err, "Could not load the partner split allocation report."));
    }
  }

  /**
   * The export is its own request, fired on the click — not fetched alongside the
   * report, same reasoning `ReportsLivestockInventory.tsx`'s own identical download
   * gives.
   */
  const [csvState, setCsvState] = useState<
    { kind: "idle" } | { kind: "busy" } | { kind: "done"; rows: number } | { kind: "error"; message: string }
  >({ kind: "idle" });

  async function downloadCsv(): Promise<void> {
    if (entityId === "") return;
    setCsvState({ kind: "busy" });
    try {
      const csv = await demoApi.getPartnerSplitsCsv({ entityId });

      // The bytes are the server writer's output verbatim — same "add a blob and a
      // filename and nothing else" discipline `ReportsLivestockInventory.tsx`'s own
      // download follows.
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "easyfarm-partner-splits.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      const rows = Math.max(csv.trim().split("\n").length - 1, 0);
      setCsvState({ kind: "done", rows });
    } catch (err) {
      setCsvState({
        kind: "error",
        message: problemMessage(err, "Could not build the partner split file."),
      });
    }
  }

  const rows = report?.rows ?? [];
  const total = rows.reduce((sum, row) => sum + row.percentage, 0);
  const isBalanced = Math.abs(total - 100) < BALANCED_TOLERANCE;

  return (
    <main className="reports-partner-splits">
      <PageHeader
        title="Partner split allocation"
        subtitle="Who owns what percentage of an entity's proceeds"
      />

      <a className="reports-partner-splits__all-reports" href="/reports">
        ← All reports
      </a>

      <section
        className="reports-partner-splits__panel"
        aria-labelledby="reports-partner-splits-heading"
        data-state={status}
      >
        <h2 id="reports-partner-splits-heading" className="reports-partner-splits__heading">
          Roster
        </h2>

        <form className="reports-partner-splits__form" onSubmit={(event) => void handleSubmit(event)}>
          <div className="reports-partner-splits__field">
            <label htmlFor={entityFieldId}>Entity</label>
            <select
              id={entityFieldId}
              value={entityId}
              onChange={(event) => setEntityId(event.target.value)}
              disabled={entities === null || entities.length === 0}
            >
              {entities === null ? (
                <option value="">Loading…</option>
              ) : entities.length === 0 ? (
                <option value="">No entities on this farm yet</option>
              ) : (
                entities.map((entity) => (
                  <option key={entity.id} value={entity.id}>
                    {entity.name}
                  </option>
                ))
              )}
            </select>
          </div>

          <button type="submit" className="btn btn--primary reports-partner-splits__show" disabled={!canSubmit}>
            {status === "loading" ? "Loading…" : "Show"}
          </button>
        </form>

        {status === "error" && (
          <StateMessage
            tone="error"
            title="Could not load the partner split allocation report."
            detail={error ?? undefined}
          />
        )}

        {status === "ready" && report !== null && (
          <>
            <div
              className="reports-partner-splits__table-wrap"
              tabIndex={0}
              role="group"
              aria-labelledby="reports-partner-splits-heading"
            >
              <table className="reports-partner-splits__table ruled-rows">
                <thead>
                  <tr>
                    <th scope="col">Partner</th>
                    <th scope="col" className="reports-partner-splits__num">
                      Percentage
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="reports-partner-splits__empty-row">
                        No active partner splits for this entity.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.id}>
                        <th scope="row" className="reports-partner-splits__partner">
                          {row.partnerName}
                        </th>
                        <td className="reports-partner-splits__num">{row.percentage.toFixed(2)}%</td>
                      </tr>
                    ))
                  )}
                </tbody>
                {rows.length > 0 && (
                  <tfoot>
                    <tr
                      className={
                        isBalanced
                          ? "reports-partner-splits__total-row"
                          : "reports-partner-splits__total-row reports-partner-splits__total-row--flagged"
                      }
                    >
                      <th scope="row">Total</th>
                      <td
                        className="reports-partner-splits__num"
                        data-state={isBalanced ? "balanced" : "unbalanced"}
                      >
                        {total.toFixed(2)}%
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {rows.length > 0 && !isBalanced && (
              <p className="reports-partner-splits__flag" role="status">
                These splits sum to {total.toFixed(2)}%, not 100% — every active row still
                renders as stored; nothing here rebalances or drops a row.
              </p>
            )}

            <div className="reports-partner-splits__export">
              <h3 className="reports-partner-splits__export-title">Partner splits for your records</h3>
              <p className="reports-partner-splits__export-copy">
                The same rows above, as a CSV a spreadsheet can open.
              </p>
              <button
                type="button"
                className="btn btn--secondary reports-partner-splits__export-btn"
                disabled={csvState.kind === "busy"}
                onClick={() => void downloadCsv()}
              >
                {csvState.kind === "busy" ? "Building the file…" : "Download CSV"}
              </button>

              {csvState.kind === "done" && (
                <p className="reports-partner-splits__export-result" role="status">
                  {csvState.rows} {csvState.rows === 1 ? "line" : "lines"} saved.
                </p>
              )}

              {csvState.kind === "error" && (
                <p className="reports-partner-splits__export-error" role="alert">
                  {csvState.message} Nothing was downloaded.
                </p>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}
